import Fuse from "fuse.js";
import type { ShopifyProduct } from "./shopify";
import {
  expandQuery,
  inferProductTags,
  normalizeText,
  tagsMatchingQuery,
  type TagId,
} from "./scentTaxonomy";

export interface IndexedProduct {
  product: ShopifyProduct;
  tagIds: TagId[];
  /** Pre-normalized text bag, used by Fuse. */
  search: {
    title: string;
    description: string;
    productType: string;
    vendor: string;
    tags: string;
    synonyms: string;
  };
}

/** خريطة نوتات العطر من قاعدتنا: handle → نصّ النوتات (عليا+وسط+قاعدة). */
export type NotesByHandle = Record<string, string | undefined>;

export function indexProducts(
  products: ShopifyProduct[],
  notes?: NotesByHandle,
): IndexedProduct[] {
  return products.map((p) => {
    const n = p.node;
    const tagIds = inferProductTags({
      title: n.title,
      description: n.description,
      productType: n.productType,
      vendor: n.vendor,
      tags: n.tags,
      notes: notes?.[n.handle],
    });
    // Pull synonyms from each inferred tag to make Fuse match cross-language queries.
    const synonyms = tagIds
      .flatMap((id) => expandQuery(id))
      .map(normalizeText)
      .join(" ");
    return {
      product: p,
      tagIds,
      search: {
        title: normalizeText(n.title || ""),
        description: normalizeText(n.description || ""),
        productType: normalizeText(n.productType || ""),
        vendor: normalizeText(n.vendor || ""),
        tags: normalizeText((n.tags || []).join(" ")),
        synonyms,
      },
    };
  });
}

function buildFuse(items: IndexedProduct[]) {
  return new Fuse(items, {
    includeScore: true,
    threshold: 0.4,
    ignoreLocation: true,
    minMatchCharLength: 2,
    keys: [
      { name: "search.title", weight: 0.45 },
      { name: "search.tags", weight: 0.18 },
      { name: "search.productType", weight: 0.12 },
      { name: "search.synonyms", weight: 0.15 },
      { name: "search.vendor", weight: 0.05 },
      { name: "search.description", weight: 0.05 },
    ],
  });
}

export interface SmartSearchOptions {
  /** Restrict to products carrying ALL these tags. */
  requiredTags?: TagId[];
  /** Restrict to products carrying ANY of these tags. */
  anyTags?: TagId[];
  limit?: number;
  /** نوتات العطر من قاعدتنا — مصدر العائلة العطريّة بدل الوصف العامّ. */
  notes?: NotesByHandle;
}

export function smartSearch(
  products: ShopifyProduct[],
  rawQuery: string,
  opts: SmartSearchOptions = {},
): ShopifyProduct[] {
  const indexed = indexProducts(products, opts.notes);
  const filtered = applyTagFilters(indexed, opts);

  const q = normalizeText(rawQuery);
  if (!q) return (opts.limit ? filtered.slice(0, opts.limit) : filtered).map((i) => i.product);

  // Tag-based boost: if the query maps to known tags, surface those products first.
  const matchedTagIds = tagsMatchingQuery(rawQuery);

  const fuse = buildFuse(filtered);
  // Use OR pattern across expanded synonyms for cross-language matching.
  const expansions = expandQuery(rawQuery).map(normalizeText).filter(Boolean);
  const pattern = expansions.length > 1 ? expansions.map((e) => `'${e}`).join(" | ") : q;
  const results = fuse.search(pattern);

  const scored = new Map<string, { item: IndexedProduct; score: number }>();
  for (const r of results) {
    const id = r.item.product.node.id;
    const tagBoost = matchedTagIds.some((t) => r.item.tagIds.includes(t)) ? -0.25 : 0;
    scored.set(id, { item: r.item, score: (r.score ?? 1) + tagBoost });
  }
  // If query only matched a tag (no fuzzy hit on any field), include products by tag.
  if (matchedTagIds.length) {
    for (const i of filtered) {
      if (matchedTagIds.some((t) => i.tagIds.includes(t)) && !scored.has(i.product.node.id)) {
        scored.set(i.product.node.id, { item: i, score: 0.35 });
      }
    }
  }

  const sorted = Array.from(scored.values()).sort((a, b) => a.score - b.score);
  const out = sorted.map((s) => s.item.product);
  return opts.limit ? out.slice(0, opts.limit) : out;
}

export function filterByTags(
  products: ShopifyProduct[],
  opts: SmartSearchOptions = {},
): ShopifyProduct[] {
  const indexed = indexProducts(products, opts.notes);
  return applyTagFilters(indexed, opts).map((i) => i.product);
}

function applyTagFilters(items: IndexedProduct[], opts: SmartSearchOptions): IndexedProduct[] {
  let out = items;
  if (opts.requiredTags?.length) {
    out = out.filter((i) => opts.requiredTags!.every((t) => i.tagIds.includes(t)));
  }
  if (opts.anyTags?.length) {
    out = out.filter((i) => opts.anyTags!.some((t) => i.tagIds.includes(t)));
  }
  return out;
}

/** Count how many products in the set carry each tag (for filter UI badges). */
export function countProductsPerTag(
  products: ShopifyProduct[],
  notes?: NotesByHandle,
): Record<TagId, number> {
  const counts = {} as Record<TagId, number>;
  for (const p of products) {
    const ids = inferProductTags({
      title: p.node.title,
      description: p.node.description,
      productType: p.node.productType,
      vendor: p.node.vendor,
      tags: p.node.tags,
      notes: notes?.[p.node.handle],
    });
    for (const id of ids) counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

/** Live suggestion strings for the search box (tag labels matching the partial query). */
export function suggestTagsForQuery(
  rawQuery: string,
  locale: "en" | "ar" | "fr",
  tagsList: { id: TagId; labels: Record<string, string>; synonyms: string[] }[],
  limit = 5,
): { id: TagId; label: string }[] {
  const q = normalizeText(rawQuery);
  if (!q) return [];
  const out: { id: TagId; label: string }[] = [];
  for (const t of tagsList) {
    const hit =
      t.synonyms.some((s) => normalizeText(s).startsWith(q) || normalizeText(s).includes(q)) ||
      Object.values(t.labels).some((l) => normalizeText(l).includes(q));
    if (hit) out.push({ id: t.id, label: t.labels[locale] ?? t.labels.en });
    if (out.length >= limit) break;
  }
  return out;
}
